import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, effect, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { Action, Store } from '@ngrx/store';
import { environment } from '../environments/environment';
import { WIDE_SCREEN_MIN_WIDTH } from './core/constants/layout-breakpoints';
import { ConfigService } from './core/services/config/config.service';
import {
  setBrowserName,
  setBrowserWidth,
  setGlobalPageSize,
  setIsMobileBrowser,
  setIsSideNavOpen,
  setTheme,
} from './core/store/actions/app.actions';
import {
  selectIsMobileBrowser,
  selectIsSideNavOpen,
  selectIsWideScreen,
  selectNavList,
  selectPageFooterData,
  selectPageHeaderData,
  selectTheme,
} from './core/store/selectors/app.selector';
import { selectNewMessageDraft } from './messages/store/selectors/messages.selector';
import { HeaderComponent } from './shared/components/layout/header/header.component';
import { SideNavComponent } from './shared/components/layout/side-nav/side-nav.component';
import { ProgressSpinnerOverlayComponent } from './shared/components/progress-spinner-overlay/progress-spinner-overlay.component';
import { setPatientId } from './user/store/actions/user.actions';
import { selectIsUserLoading, selectPatientId, selectUser } from './user/store/selectors/user.selectors';

const ROW_HEIGHT = 72; // estimated row height in px
const PAGE_MULTIPLIER = 2;
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [HeaderComponent, ProgressSpinnerOverlayComponent, RouterModule, SideNavComponent],
})
export class AppComponent implements OnInit, OnDestroy {
  private store = inject(Store);
  private breakpointObserver = inject(BreakpointObserver);
  private configService = inject(ConfigService);
  isLocalAuthBypass = environment.allowAuthBypass && this.configService.config?.localAuthBypass === true;

  pageHeaderData = this.store.selectSignal(selectPageHeaderData);
  user = this.store.selectSignal(selectUser);
  selectedPatientId = this.store.selectSignal(selectPatientId);
  theme = this.store.selectSignal(selectTheme);
  navList = this.store.selectSignal(selectNavList);
  isLoading = this.store.selectSignal(selectIsUserLoading);
  isWideScreen = this.store.selectSignal(selectIsWideScreen);
  isMobileBrowser = this.store.selectSignal(selectIsMobileBrowser);
  isSideNavOpen = this.store.selectSignal(selectIsSideNavOpen);
  patientId = this.store.selectSignal(selectPatientId);
  pageFooterData = this.store.selectSignal(selectPageFooterData);
  newMessageDraft = this.store.selectSignal(selectNewMessageDraft);

  title = 'Patient Portal';
  private mediaQuery: MediaQueryList = window.matchMedia('(prefers-color-scheme: dark)');
  private isPrinting = false;
  private onBeforePrint = () => {
    this.isPrinting = true;
  };
  private onAfterPrint = () => {
    setTimeout(() => {
      this.isPrinting = false;
    }, 500);
  };
  private hasReplayedRoutePatientId = false;

  constructor() {
    const routePatientId = this.readRoutePatientId();
    if (routePatientId !== null) {
      effect(() => this.replayRoutePatientId(routePatientId));
    }
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | null;
    this.store.dispatch(setTheme({ theme: savedTheme ?? (this.mediaQuery.matches ? 'dark' : 'light') }));
    this.mediaQuery.addEventListener('change', this.themeChangeListener);
    window.addEventListener('beforeprint', this.onBeforePrint);
    window.addEventListener('afterprint', this.onAfterPrint);
    this.updateFavIcon();

    this.breakpointObserver
      .observe([`(max-width: ${WIDE_SCREEN_MIN_WIDTH - 1}px)`])
      .pipe(takeUntilDestroyed())
      .subscribe(result => {
        this.store.dispatch(setIsMobileBrowser({ isMobileBrowser: result.matches }));
      });

    this.store.dispatch(setBrowserName());
    this.setGlobalPageSize();
  }

  ngOnInit() {
    this.store.dispatch(setBrowserWidth({ width: window.innerWidth }));
  }

  /** The patientId in `/patient/:patientId/...`, or null when the deep link isn't patient-scoped. */
  private readRoutePatientId(): number | null {
    const activeRoutes = window.location.pathname.split('/');
    if (!activeRoutes.includes('patient')) {
      return null;
    }
    return +activeRoutes[activeRoutes.indexOf('patient') + 1];
  }

  /**
   * Re-selects the patient named in the URL once the user has loaded — the store starts empty on a
   * hard refresh, so a deep link would otherwise render against no selected patient.
   *
   * Gated on the user being *present*, not on `isLoading` going false: `getUserFailure` and
   * `setGlobalError` also settle that flag, and there the valid patient ids are still empty, so the
   * replay would spend its one shot on a `setPatientId` that setPatientId$ can only route to
   * /invalid-patient — and a later successful load would never re-apply the URL's patient id.
   *
   * Latched because `user` survives the later reloads (e.g. silent login) that flip `isLoading` back
   * on, and replaying then would re-run the switch pipeline over whatever the patient is doing.
   * Marked `page-load` so the pipeline treats it as an adoption rather than a switch — see
   * PatientIdChangeTrigger.
   */
  private replayRoutePatientId(patientId: number): void {
    if (this.user() === null || this.hasReplayedRoutePatientId) {
      return;
    }
    this.hasReplayedRoutePatientId = true;
    this.store.dispatch(setPatientId({ patientId, trigger: 'page-load' }));
  }

  ngOnDestroy() {
    this.mediaQuery.removeEventListener('change', this.themeChangeListener);
    window.removeEventListener('beforeprint', this.onBeforePrint);
    window.removeEventListener('afterprint', this.onAfterPrint);
  }

  dispatchSideNavAction(action: Action) {
    this.store.dispatch(action);
  }

  setSideNavOpen(isSideNavOpen: boolean): void {
    this.store.dispatch(setIsSideNavOpen({ isSideNavOpen }));
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.store.dispatch(setBrowserWidth({ width: window.innerWidth }));
    this.setGlobalPageSize();
  }

  private themeChangeListener = () => {
    if (this.isPrinting) return;
    this.store.dispatch(setTheme({ theme: this.mediaQuery.matches ? 'dark' : 'light' }));
    this.updateFavIcon();
  };

  private updateFavIcon() {
    //set favicon based on browser color settings
    const link = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
    if (link) {
      link.href = this.mediaQuery.matches ? 'images/favicon-dark.png' : 'images/favicon-light.png';
    }
  }

  private setGlobalPageSize(): void {
    if (typeof window !== 'undefined') {
      const rowsThatFit = Math.ceil(window.innerHeight / ROW_HEIGHT);
      const initialPageSize = rowsThatFit * PAGE_MULTIPLIER;
      this.store.dispatch(setGlobalPageSize({ pageSize: initialPageSize }));
    }
  }
}
